import { countries } from "@/app/components/settings/countries";

const URL_PREFIX_RE = /^(?:https?:\/\/|www\.|linkedin\.com\b)/i;
const URL_TOKEN_RE = /^(?:https?|www|linkedin|linkedin\.com|com)$/i;

export function getCandidateCompany(candidate) {
  const raw = String(candidate?.company || "").trim();
  if (!raw) return "";
  if (URL_PREFIX_RE.test(raw)) return "";
  if (URL_TOKEN_RE.test(raw.toLowerCase())) return "";
  return raw;
}

const COUNTRY_CODE_TO_NAME = Object.fromEntries(
  (countries || [])
    .filter((entry) => entry?.value && entry?.label)
    .map((entry) => [String(entry.value).toUpperCase(), String(entry.label)]),
);

const COUNTRY_NAME_TO_CODE = Object.fromEntries(
  (countries || [])
    .filter((entry) => entry?.value && entry?.label)
    .map((entry) => [
      normalizeCountryKey(entry.label),
      String(entry.value).toUpperCase(),
    ]),
);

const COUNTRY_ALPHA3_TO_ALPHA2 = {
  USA: "US",
  CAN: "CA",
  GBR: "GB",
  AUS: "AU",
  DEU: "DE",
  FRA: "FR",
  IND: "IN",
  NLD: "NL",
  SWE: "SE",
  CHE: "CH",
  SGP: "SG",
  BRA: "BR",
  ESP: "ES",
  ITA: "IT",
  ISR: "IL",
  NOR: "NO",
  DNK: "DK",
  FIN: "FI",
  NZL: "NZ",
  ZAF: "ZA",
  JPN: "JP",
  KOR: "KR",
  CHN: "CN",
  IRL: "IE",
  PRT: "PT",
  AUT: "AT",
  BEL: "BE",
  POL: "PL",
  MEX: "MX",
  ARG: "AR",
  COL: "CO",
  CHL: "CL",
  ARE: "AE",
  NGA: "NG",
  KEN: "KE",
  PAK: "PK",
  UKR: "UA",
};

const COUNTRY_TEXT_ALIASES_TO_CODE = {
  us: "US",
  usa: "US",
  "united states": "US",
  "united states of america": "US",
  uk: "GB",
  uae: "AE",
};

function normalizeCountryKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ");
}

function toDisplayCountryLabel(countryCode, fallback = "") {
  const normalizedCode = String(countryCode || "")
    .trim()
    .toUpperCase();
  if (!normalizedCode) return String(fallback || "").trim();

  const fromHelper = COUNTRY_CODE_TO_NAME[normalizedCode];
  if (fromHelper) return fromHelper;

  if (typeof Intl === "undefined" || typeof Intl.DisplayNames !== "function") {
    return String(fallback || "").trim();
  }

  try {
    const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
    const resolved = displayNames.of(normalizedCode) || "";
    if (!resolved) return String(fallback || "").trim();
    return resolved;
  } catch {
    return String(fallback || "").trim();
  }
}

export function resolveCountryCode(value = "") {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const uppercase = rawValue.toUpperCase();
  if (/^[A-Z]{2}$/.test(uppercase)) return uppercase;

  if (/^[A-Z]{3}$/.test(uppercase)) {
    return COUNTRY_ALPHA3_TO_ALPHA2[uppercase] || "";
  }

  const key = normalizeCountryKey(rawValue);
  return COUNTRY_TEXT_ALIASES_TO_CODE[key] || COUNTRY_NAME_TO_CODE[key] || "";
}

function getCountryNameFromCode(code) {
  const normalizedCode = resolveCountryCode(code);
  if (!normalizedCode) return "";
  return toDisplayCountryLabel(normalizedCode, "");
}

export function normalizeCandidateCountry(country, countryCode = "") {
  const directCountry = String(country || "").trim();

  if (directCountry) {
    const resolvedFromCountry = resolveCountryCode(directCountry);
    if (resolvedFromCountry) {
      return toDisplayCountryLabel(resolvedFromCountry, directCountry);
    }
    return directCountry;
  }

  const rawCountryCode = String(countryCode || "").trim();
  if (!rawCountryCode) return "";

  // Some records store full country text in countryCode-like fields.
  const fromCode = getCountryNameFromCode(rawCountryCode);
  if (fromCode) return fromCode;

  return rawCountryCode;
}

export function hasDisplayCompany(candidate) {
  const company = getCandidateCompany(candidate).toLowerCase();
  return (
    Boolean(company) &&
    !URL_PREFIX_RE.test(company) &&
    !URL_TOKEN_RE.test(company) &&
    !/^[-–—/]+$/.test(company) &&
    !/^(no company|company not|not listed|unknown|n\/a)\b/.test(company)
  );
}

export function getCandidateTitle(candidate) {
  const rawTitle = [
    candidate?.currentPositionTitle,
    candidate?.title,
    candidate?.headline,
    candidate?.position,
    candidate?.jobTitle,
  ].find((value) => {
    if (value === null || value === undefined) return false;
    const text = String(value).trim();
    return text.length > 0 && !/^(role not listed|unknown|n\/a)$/i.test(text);
  });

  return String(rawTitle || "").trim();
}

export function hasUsableCandidateDetails(candidate) {
  const fullName = `${candidate?.firstName || ""} ${candidate?.lastName || ""}`
    .trim()
    .toLowerCase();
  const title = getCandidateTitle(candidate).toLowerCase();

  const hasRealName =
    fullName.length > 0 && !/^(unknown|n\/a|not listed)\b/.test(fullName);
  const hasRealTitle =
    title.length > 0 && !/^(role not listed|unknown|n\/a)\b/.test(title);

  return hasDisplayCompany(candidate) || hasRealName || hasRealTitle;
}

export function toRawCandidateFromSearchResult(result) {
  const expertiseList = Array.isArray(result?.expertise)
    ? result.expertise
    : [];
  const expertiseTitle =
    typeof expertiseList[0] === "string" ? expertiseList[0].trim() : "";
  const normalizedCountry = normalizeCandidateCountry(
    result?.country || "",
    result?.countryCode || "",
  );
  const resolvedTitle =
    expertiseTitle ||
    result?.jobTitle ||
    result?.currentPositionTitle ||
    result?.headline ||
    "";

  return {
    enriched: result?.enriched === true || result?.externalEnriched === true,
    firstName: result?.given_name || "",
    lastName: result?.family_name || "",
    currentPositionTitle: resolvedTitle,
    title: resolvedTitle,
    company: result?.company || result?.companyAff || "",
    companyDetails: result?.companyDetails,
    location: result?.location || "",
    country: normalizedCountry,
    countryCode: result?.countryCode || "",
    linkedinUrl: result?.linkedinUrl || "",
    profilePictureUrl:
      result?.picture_custom ||
      result?.picture_cached ||
      result?.picture ||
      result?.profilePictureUrl ||
      "",
  };
}
